import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import {
	DocumentType,
	PreUploadIDCard,
	UploadIDCard,
	Name,
	Sex,
	Birthday,
	Nationality,
	DocumentNumber,
	PlaceOfResidence,
	MainlandBankCard,
	Policies,
	PreloadedStaticImg,
	TaxResident,
	SurveyHeaderProgressBar,
} from "@/containers/survey/account-opening";
import SurveyHeader from "@/containers/survey/SurveyHeader";

import { useAnswers, useMasterAccount, useOCR, useQuestions } from "@/hooks/useRequest/useSurveyRequest";
import { SurveyType } from "@/interfaces/surveyTypes";
import RFLayout from "@/layout/RFLayout";
import { getAccountOpeningSurveyPageNumber } from "@/utils/util";
import { BridgeCallHandlers, OpenAccountRejectInitialReviewReasonTypes } from "@/interfaces/enums";
import bridge from "@/utils/dsBridge";

import SkeletonWrapper from "@/containers/survey/SkeletonWrapper";
import useInApp from "@/hooks/useIsApp";
import ContentContainer from "@/containers/common/ContentContainer";
import { useAppSetting } from "@/service/common.services";
import { SurveyHeaderProgressBarInstance } from "@/containers/survey/account-opening/SurveyHeaderProgressBar";

const timeStream = Date.now();

export default function AccountOpeningPage() {
	const isInApp = useInApp();
	const surveyBarRef = useRef<SurveyHeaderProgressBarInstance>(null);
	const [pageNumber, setPageNumber] = useState(1);
	const [identity, setIdentity] = useState<number>(); // fix 切换证件，导致下一页UI变化

	const { data: settingData, isLoading: settingIsLoading } = useAppSetting(timeStream);
	const { questionsLoading, questions, questionFieldToIdMap, questionFieldToUiSchemaMap, mutate } = useQuestions({
		type: SurveyType.ACCOUNT_OPENING,
	});
	const {
		answersLoading,
		answers,
		answerFieldToValueMap,
		mutate: answerMutate,
	} = useAnswers({
		type: SurveyType.ACCOUNT_OPENING,
	});
	const { OCR, mutate: ORCMutate } = useOCR();
	const { masterAccount, masterAccountLoading, mutate: masterAccountMutate } = useMasterAccount();
	const [direction, setDirection] = useState<"forward" | "backward">("forward");

	const memoIpRegion = useMemo(() => {
		if (settingIsLoading || !settingData) return undefined;
		for (const item of settingData) {
			if (item.key === "country") {
				return String(item.value);
			}
		}
		return undefined;
	}, [settingIsLoading]);

	useEffect(() => {
		if (!questions || !answers || !masterAccount) {
			return;
		}

		const jumpPageNumber = getAccountOpeningSurveyPageNumber({
			answers,
			masterAccount,
		});

		setPageNumber(jumpPageNumber);
	}, [answersLoading, questionsLoading, masterAccountLoading]);

	const nextPage = () => {
		const nextPageNumber = pageNumber + 1;
		mutate();
		answerMutate();
		setPageNumber(nextPageNumber);
		setDirection("forward");
	};

	const prePage = () => {
		setDirection("backward");

		if (pageNumber === 12 && !isChinaMainland()) {
			setPageNumber(pageNumber - 2);
			return;
		}

		const prePage = pageNumber - 1;
		if (prePage < 1) {
			closePage();
			return;
		}
		setPageNumber(prePage);
	};

	const closePage = () => {
		if (isInApp) {
			bridge.call(BridgeCallHandlers.ON_CLOSE_WEB_PAGE);
		} else {
			window.history.back();
		}
	};

	// 用户协议返回
	const isChinaMainland = () => {
		const nationality = answerFieldToValueMap.get("nationality") || "";
		const countryOfResidence = answerFieldToValueMap.get("countryOfResidence") || "";

		return nationality === "CN" || countryOfResidence === "CN";
	};

	const renderQuestionPage = () => {
		switch (pageNumber) {
			case 1:
				return (
					<Nationality
						questionFieldToIdMap={questionFieldToIdMap}
						answerFieldToValueMap={answerFieldToValueMap}
						onNextClick={nextPage}
						ipRegion={memoIpRegion}
					/>
				);
			case 2:
				return (
					<TaxResident
						questionFieldToIdMap={questionFieldToIdMap}
						answerFieldToValueMap={answerFieldToValueMap}
						nationality={answerFieldToValueMap.get("nationality") || ""}
						onNextClick={nextPage}
					/>
				);
			case 3:
				return (
					<DocumentType
						questionFieldToIdMap={questionFieldToIdMap}
						answerFieldToValueMap={answerFieldToValueMap}
						questionFieldToUiSchemaMap={questionFieldToUiSchemaMap}
						ocrResult={masterAccount?.ocrStatus}
						ocrIdentity={OCR?.identityType}
						onNextClick={(val) => {
							setIdentity(val);
							nextPage();
						}}
					/>
				);
			case 4:
				return (
					<PreUploadIDCard answerFieldToValueMap={answerFieldToValueMap} identity={identity} onNextClick={nextPage} />
				);
			case 5:
				return (
					<UploadIDCard
						answerFieldToValueMap={answerFieldToValueMap}
						ocrResult={masterAccount?.ocrStatus}
						identityFirstImage={OCR?.identityFirstImage}
						identitySecondImage={OCR?.identitySecondImage}
						ocrIdentity={OCR?.identityType}
						onNextClick={() => {
							ORCMutate();
							masterAccountMutate();
							nextPage();
						}}
					/>
				);
			case 6:
				return (
					<Name
						onNextClick={nextPage}
						questionFieldToIdMap={questionFieldToIdMap}
						answerFieldToValueMap={answerFieldToValueMap}
						orcFirstName={OCR?.givenNameEn}
						orcMiddleName={OCR?.middleNameEn}
						orcLastName={OCR?.givenNameEn}
					/>
				);
			case 7:
				return (
					<Sex
						questionFieldToIdMap={questionFieldToIdMap}
						answerFieldToValueMap={answerFieldToValueMap}
						questionFieldToUiSchemaMap={questionFieldToUiSchemaMap}
						orcGender={OCR?.gender}
						onNextClick={nextPage}
					/>
				);
			case 8:
				return (
					<Birthday
						questionFieldToIdMap={questionFieldToIdMap}
						answerFieldToValueMap={answerFieldToValueMap}
						onNextClick={nextPage}
					/>
				);

			case 9:
				return (
					<DocumentNumber
						questionFieldToIdMap={questionFieldToIdMap}
						answerFieldToValueMap={answerFieldToValueMap}
						orcIdentityNo={OCR?.identityNo}
						ocrIdentity={OCR?.identityType}
						onNextClick={nextPage}
					/>
				);
			case 10:
				return (
					<PlaceOfResidence
						questionFieldToIdMap={questionFieldToIdMap}
						answerFieldToValueMap={answerFieldToValueMap}
						onNextClick={(isChinaMainland) => {
							if (isChinaMainland) {
								nextPage();
							} else {
								setPageNumber(pageNumber + 2);
								setDirection("forward");
							}
						}}
					/>
				);
			case 11:
				return (
					<MainlandBankCard hasNonMainlandBankCard={masterAccount?.hasNonMainlandBankCard} onNextClick={nextPage} />
				);
			case 12:
				return (
					<Policies
						unRejectedSubmissions={masterAccount?.rejectReason === OpenAccountRejectInitialReviewReasonTypes.DEFAULT}
						afterSubmit={async () => {
							if (surveyBarRef.current) {
								return await surveyBarRef.current.onComplete();
							}
							return Promise.resolve(true);
						}}
					/>
				);
			default:
				return <div>Not Found</div>;
		}
	};

	return (
		<>
			<PreloadedStaticImg />
			<ContentContainer className="zh-letter-space" header={isInApp && <SurveyHeader onBackClick={prePage} />}>
				{answersLoading || questionsLoading || masterAccountLoading ? (
					<SkeletonWrapper />
				) : (
					<ContentContainer
						header={
							<SurveyHeaderProgressBar direction={direction} page={pageNumber} totalPage={12} ref={surveyBarRef} />
						}
					>
						{renderQuestionPage()}
					</ContentContainer>
				)}
			</ContentContainer>
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async ({ locale = "en" }) => {
	return {
		props: {
			...(await serverSideTranslations(locale, ["survey", "policy", "error", "common"])),
		},
	};
};

AccountOpeningPage.getLayout = function getLayout(page: ReactElement) {
	return <RFLayout>{page}</RFLayout>;
};
